import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { cloneDeep } from "lodash";
import { Toast } from "primereact/toast";
import { ProgressSpinner } from "primereact/progressspinner";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { TextInputWithIcon } from "../../components/TextInputWithIcon/TextInputWithIcon";
import { DateInput } from "../../components/DateInput/DateInput";
import { DropdownInput } from "../../components/DropdownInput/DropdownInput";
import { TextAreaInput } from "../../components/TextAreaInput/TextAreaInput";
import { PdfPreview } from "../../components/PdfPreview/PDFPreview";
import { RadioButtonInput } from "../../components/RadioButtonInput/RadioButtonInput";
import { getOwnershipDocumentTemplate } from "../../document-templates/OwnershipDocumentTemplate";
import { OwnershipDocument } from "../../models/OwnershipDocument";
import { Product, ProductType, ProductCategory } from "../../models/Product";
import { Warehouse } from "../../models/Warehouse";
import { Partner } from "../../models/Partner";
import { ParamSettings } from "../../models/ParamSettings";
import { MercantileType } from "../../models/ParamSettings";
import { srpsCalculation } from "../../services/SprsService";
import { getAllWarehouses } from "../../services/WarehouseService";
import { getAllPartners } from "../../services/PartnerService";
import { getAllProductCategories } from "../../services/ProductCategoryService";
import { getAllProducts } from "../../services/ProductService";
import {
  getOneOwnershipDocument,
  createOwnershipDocument,
  updateOneOwnershipDocument,
} from "../../services/OwnershipDocumentService";
import { getAllSettings } from "../../services/SettingsService";
import "./OwnershipDocumentPage.scss";

export interface OwnershipDocumentPageProps {
  show?: string;
  buttonSaveStyle?: React.CSSProperties;
  buttonNewPartnerStyle?: React.CSSProperties;
  buttonResetStyle?: React.CSSProperties;
}

export const OwnershipDocumentPage = (p: OwnershipDocumentPageProps) => {
  const { buttonSaveStyle, buttonResetStyle } = p;

  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const toast = useRef<any>(null);

  const [updating, setUpdating] = useState<boolean>(false);
  const [processing, setProcessing] = useState(false);

  const [warehousesApi, setWarehousesApi] = useState<Warehouse[]>([]);
  const [partnersApi, setPartnersApi] = useState<Partner[]>([]);
  const [productCategoriesApi, setProductCategoriesApi] = useState<
    ProductCategory[]
  >([]);
  const [productsApi, setProductsApi] = useState<Product[]>([]);
  const [settingsApi, setSettingsApi] = useState<ParamSettings[]>([]);

  const [newOwnershipDocument, setNewOwnershipDocument] =
    useState<OwnershipDocument>({
      id: id ?? "",
      warehouse: {} as Warehouse,
      date: new Date(),
      netto: undefined,
      srps: 0,
      product: {} as Product,
      mercantile: true,
      previousOwner: {} as Partner,
      newOwner: {} as Partner,
      moisture: undefined,
      impurity: undefined,
      breakage: undefined,
      defect: undefined,
      hectoliter: undefined,
      protein: undefined,
      calculationMethod: true,
      note: "",
    });

  const [productCategory, setProductCategory] = useState<ProductCategory>();

  const [pdfData, setPdfData] = useState<any>(
    getOwnershipDocumentTemplate(newOwnershipDocument, settingsApi)
  );

  const downloadPdfDocument = useRef<any>(null);
  const printPdfDocument = useRef<any>(null);

  const ensureSuccess = (resp: any) => {
    if (resp?.data?.status === "ERROR") {
      throw new Error(resp?.data?.message ?? "Greška");
    }
  };

  const refreshLookups = async () => {
    const [wResp, partnerResp, pcResp, pResp] = await Promise.all([
      getAllWarehouses(),
      getAllPartners(),
      getAllProductCategories(),
      getAllProducts(),
    ]);

    [wResp, partnerResp, pcResp, pResp].forEach(ensureSuccess);

    setWarehousesApi(wResp?.data?.result ?? []);
    setPartnersApi(partnerResp?.data?.result ?? []);
    setProductCategoriesApi(pcResp?.data?.result ?? []);
    setProductsApi(pResp?.data?.result ?? []);
  };

  const refreshSettings = async () => {
    const sResp = await getAllSettings();
    ensureSuccess(sResp);
    setSettingsApi(sResp?.data?.result ?? []);
  };

  useEffect(() => {
    setProcessing(true);

    Promise.all([refreshSettings(), refreshLookups()])
      .catch((e: any) => {
        toast.current?.show({
          severity: "error",
          detail: e?.message ?? String(t("Greška pri učitavanju podataka")),
          life: 3500,
        });
      })
      .finally(() => setProcessing(false));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!id) return;

    setProcessing(true);
    getOneOwnershipDocument(id)
      .then((resp) => {
        if (resp?.data?.status === "ERROR") return;

        const doc = resp?.data?.result;
        if (!doc) return;

        const normalized: any = { ...doc };
        if (normalized.date) normalized.date = new Date(normalized.date);

        setNewOwnershipDocument(normalized);
        setProductCategory(normalized?.product?.category);
        setUpdating(true);
      })
      .catch(() => {
        setUpdating(false);
      })
      .finally(() => setProcessing(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // populate pdf when data changes
  useEffect(() => {
    const newPdf = getOwnershipDocumentTemplate(newOwnershipDocument, settingsApi);
    setPdfData(newPdf);
  }, [newOwnershipDocument, settingsApi]);

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
  } = useForm({
    mode: "all",
  });

  const onSubmit = async () => {
    // calculation srps (ostalo isto)
    let categoryType: MercantileType = MercantileType.Wheat;
    if (newOwnershipDocument.product?.category?.name === "Uljana repica")
      categoryType = MercantileType.OiledBeet;
    if (newOwnershipDocument.product?.category?.name === "Ječam")
      categoryType = MercantileType.Barley;
    if (newOwnershipDocument.product?.category?.name === "Soja")
      categoryType = MercantileType.Soya;
    if (newOwnershipDocument.product?.category?.name === "Suncokret")
      categoryType = MercantileType.Sunflower;
    if (newOwnershipDocument.product?.category?.name === "Kukuruz")
      categoryType = MercantileType.Corn;

    const copy = cloneDeep(newOwnershipDocument);
    if (newOwnershipDocument.calculationMethod) {
      copy.srps = srpsCalculation(
        categoryType,
        newOwnershipDocument.netto ?? 0,
        newOwnershipDocument?.moisture ?? 0,
        newOwnershipDocument.impurity ?? 0,
        newOwnershipDocument.breakage ?? 0,
        newOwnershipDocument.defect ?? 0,
        newOwnershipDocument.hectoliter as number,
        true,
        settingsApi
      )[0];
    } else {
      copy.srps = newOwnershipDocument.netto as number;
    }

    setProcessing(true);
    try {
      if (updating) {
        const resp = await updateOneOwnershipDocument(copy.id, copy);
        if (resp?.data?.status === "ERROR") {
          throw new Error(resp?.data?.message ?? "Greška pri snimanju");
        }
      } else {
        const resp = await createOwnershipDocument(copy);
        if (resp?.data?.status === "ERROR") {
          throw new Error(resp?.data?.message ?? "Greška pri snimanju");
        }
      }

      // @ts-ignore
      toast.current?.show({
        severity: "success",
        detail: t(`Uspešno popunjen vlasnički list`),
        life: 3000,
      });

      navigate(`/ownership-documents`);
    } catch (e: any) {
      // @ts-ignore
      toast.current?.show({
        severity: "error",
        detail: e?.message ?? String(t("Greška pri snimanju")),
        life: 3500,
      });
    } finally {
      setProcessing(false);
    }
  };

  const onError = () => {
    toast.current?.show({
      severity: "error",
      detail: t("Neispravno popunjena polja"),
      life: 3000,
    });
  };

  const handleResetForm = () => {
    setNewOwnershipDocument({
      id: id ?? "",
      warehouse: {} as Warehouse,
      date: new Date(),
      netto: 0,
      srps: 0,
      product: {} as Product,
      mercantile: true,
      previousOwner: {} as Partner,
      newOwner: {} as Partner,
      moisture: undefined,
      impurity: undefined,
      breakage: undefined,
      defect: undefined,
      hectoliter: undefined,
      protein: undefined,
      calculationMethod: true,
      note: "",
    });
    setProductCategory(undefined);
  };

  const handleNewOwnershipDocument = (prop: string, value: any) => {
    setNewOwnershipDocument((prev) => ({
      ...prev,
      [prop]: value,
    }));
  };

  const handlePreviousOwnerChange = (prop: string, value: any) => {
    const copy = cloneDeep(newOwnershipDocument);
    copy.previousOwner = partnersApi.find(
      (item) => item.id == value
    ) as Partner;
    setNewOwnershipDocument(copy);
  };

  const handleNewOwnerChange = (prop: string, value: any) => {
    const copy = cloneDeep(newOwnershipDocument);
    copy.newOwner = partnersApi.find((item) => item.id == value) as Partner;
    setNewOwnershipDocument(copy);
  };

  const handleWarehouseChange = (prop: string, value: any) => {
    const copy = cloneDeep(newOwnershipDocument);
    copy.warehouse = warehousesApi.find(
      (item) => item.id == value
    ) as Warehouse;
    setNewOwnershipDocument(copy);
  };

  const handleProductCategoryChange = (prop: string, value: any) => {
    const pc = productCategoriesApi.find(
      (item) => item.id == value
    ) as ProductCategory;
    setProductCategory(pc);

    const copy = cloneDeep(newOwnershipDocument);
    copy.product = {} as Product;
    setNewOwnershipDocument(copy);
  };

  const handleProductChange = (prop: string, value: any) => {
    const copy = cloneDeep(newOwnershipDocument);
    copy.product = productsApi.find((item) => item.id == value) as Product;
    setNewOwnershipDocument(copy);
  };

  let addButtonSaveStyle: React.CSSProperties = {};
  if (buttonSaveStyle) {
    addButtonSaveStyle = buttonSaveStyle;
  } else {
    addButtonSaveStyle.background = "rgba(0, 165, 83, 1)";
    addButtonSaveStyle.border = "none";
    addButtonSaveStyle.margin = "0 .5rem";
  }

  let addButtonResetStyle: React.CSSProperties = {};
  if (buttonResetStyle) {
    addButtonResetStyle = buttonResetStyle;
  } else {
    addButtonResetStyle.background = "rgba(220, 53, 69, 1)";
    addButtonResetStyle.border = "none";
  }

  return (
    <PageLayout>
      <Toast ref={toast} position={"top-right"} />
      <div className="form-wrapper">
        <div className="form-header">
          <div className="header-title">
            <i
              className="bi bi-file-text"
              style={{ fontSize: 16, marginRight: 6 }}
            ></i>
            <span className="header-title">
              {`${t("Vlasnički list broj")}:`}{" "}
              {newOwnershipDocument?.ownerDocNumberFormatted ??
                newOwnershipDocument.ownerDocId}
            </span>
          </div>
          <div className="header-buttons">
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
                margin: "0 .5rem",
              }}
              labelText={t("Svi vlasnički listovi")}
              icon="bi bi-arrow-left-square"
              onButtonClick={() => navigate(`/ownership-documents`)}
            />
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
                margin: "0 .5rem",
              }}
              labelText={""}
              icon="bi bi-file-earmark-pdf"
              onButtonClick={() => downloadPdfDocument.current?.()}
              disabled={
                !(
                  newOwnershipDocument?.ownerDocNumberFormatted ||
                  newOwnershipDocument.ownerDocId
                )
              }
            />
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
              }}
              labelText={""}
              icon="bi bi-printer"
              onButtonClick={() => printPdfDocument.current?.()}
              disabled={
                !(
                  newOwnershipDocument?.ownerDocNumberFormatted ||
                  newOwnershipDocument.ownerDocId
                )
              }
            />
          </div>
        </div>

        <div className="form-body">
          {processing ? (
            <ProgressSpinner />
          ) : (
            <div className="form-input-fields">
              <DropdownInput
                id="warehouse"
                value={newOwnershipDocument.warehouse.id}
                onValueChange={handleWarehouseChange}
                optionValue="id"
                optionLabel="name"
                options={warehousesApi.filter((item) => item.virtual !== true)}
                filter={false}
                labelText={`${t("Magacin")} *`}
                placeholderText={t("Izaberite iz liste")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
              />
              <DateInput
                id="date"
                value={newOwnershipDocument.date as Date}
                onValueChange={handleNewOwnershipDocument}
                labelText={`${t("Datum")} *`}
                placeholderText={t("Izaberite datum")}
                icon="bi bi-calendar-check"
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
              />
              <DropdownInput
                id="previousOwner"
                value={newOwnershipDocument.previousOwner?.id as string}
                optionValue="id"
                optionLabel="name"
                onValueChange={handlePreviousOwnerChange}
                options={partnersApi}
                filter={true}
                labelText={`${t("Prethodni vlasnik")} *`}
                placeholderText={t("Izaberite iz liste")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
              />
              <DropdownInput
                id="newOwner"
                value={newOwnershipDocument.newOwner?.id as string}
                optionValue="id"
                optionLabel="name"
                onValueChange={handleNewOwnerChange}
                options={partnersApi}
                filter={true}
                labelText={`${t("Novi vlasnik")} *`}
                placeholderText={t("Izaberite iz liste")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
              />
              <DropdownInput
                id="productCategory"
                value={productCategory?.id}
                onValueChange={handleProductCategoryChange}
                optionValue="id"
                optionLabel="name"
                options={productCategoriesApi.filter(
                  (item) => item.type == ProductType.MercantileGoods
                )}
                filter={false}
                labelText={`${t("Vrsta robe")} *`}
                placeholderText={t("Izaberite iz liste")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
              />
              <DropdownInput
                id="productName"
                value={newOwnershipDocument.product?.id}
                onValueChange={handleProductChange}
                optionValue="id"
                optionLabel="name"
                options={productsApi.filter(
                  (item) => item.category?.id === productCategory?.id
                )}
                filter={false}
                labelText={`${t("Naziv robe")} *`}
                placeholderText={t("Izaberite iz liste")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
              />
              <TextInputWithIcon
                id="netto"
                value={newOwnershipDocument.netto as number}
                onValueChange={handleNewOwnershipDocument}
                labelText={`${t("Količina")} *`}
                icon="bi bi-clipboard"
                iconAlign="right"
                placeholderText={t("Unesite količinu")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{
                  pattern: { value: /^\d+$/, message: "Please enter a number" },
                }}
              />
              <div></div>
              <TextInputWithIcon
                id="moisture"
                value={(newOwnershipDocument.moisture as number) ?? ""}
                onValueChange={handleNewOwnershipDocument}
                labelText={`${t("Vlaga")}`}
                icon="bi bi-clipboard"
                iconAlign="right"
                placeholderText={t(
                  "Unesite procenat vlage kao ceo broj, sa jednom ili dve decimale (npr 11.90)"
                )}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{
                  min: 0,
                  max: 100,
                  pattern: {
                    value: /^\d+(\.\d{1,2})?$/,
                    message: "Please enter a number",
                  },
                }}
              />
              <TextInputWithIcon
                id="impurity"
                value={newOwnershipDocument.impurity as number}
                onValueChange={handleNewOwnershipDocument}
                labelText={`${t("Primese")}`}
                icon="bi bi-clipboard"
                iconAlign="right"
                placeholderText={t(
                  "Unesite procenat primesa kao ceo broj, sa jednom ili dve decimale"
                )}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{
                  min: 0,
                  max: 100,
                  pattern: {
                    value: /^\d+(\.\d{1,2})?$/,
                    message: "Please enter a number",
                  },
                }}
              />
              {newOwnershipDocument?.product?.category?.name === "Kukuruz" && (
                <TextInputWithIcon
                  id="breakage"
                  value={(newOwnershipDocument.breakage as number) ?? ""}
                  onValueChange={handleNewOwnershipDocument}
                  labelText={t("Lom")}
                  icon="bi bi-clipboard"
                  iconAlign="right"
                  placeholderText={t(
                    "Unesite procenat loma kao ceo broj, sa jednom ili dve decimale"
                  )}
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  rules={{
                    min: 0,
                    max: 100,
                    pattern: {
                      value: /^\d+(\.\d{1,2})?$/,
                      message: "Please enter a number",
                    },
                  }}
                />
              )}
              {newOwnershipDocument?.product?.category?.name === "Kukuruz" && (
                <TextInputWithIcon
                  id="defect"
                  value={(newOwnershipDocument.defect as number) ?? ""}
                  onValueChange={handleNewOwnershipDocument}
                  labelText={t("Defekt")}
                  icon="bi bi-clipboard"
                  iconAlign="right"
                  placeholderText={t(
                    "Unesite procenat defekta kao ceo broj, sa jednom ili dve decimale"
                  )}
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  rules={{
                    min: 0,
                    max: 100,
                    pattern: {
                      value: /^\d+(\.\d{1,2})?$/,
                      message: "Please enter a number",
                    },
                  }}
                />
              )}
              {(newOwnershipDocument?.product?.category?.name === "Pšenica" ||
                newOwnershipDocument?.product?.category?.name === "Ječam") && (
                <TextInputWithIcon
                  id="hectoliter"
                  value={(newOwnershipDocument.hectoliter as number) ?? ""}
                  onValueChange={handleNewOwnershipDocument}
                  labelText={t("Hektolitar")}
                  icon="bi bi-clipboard"
                  iconAlign="right"
                  placeholderText={t(
                    "Unesite hektolitar kao ceo broj, sa jednom ili dve decimale"
                  )}
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  rules={{
                    min: 0,
                    max: 100,
                    pattern: {
                      value: /^\d+(\.\d{1,2})?$/,
                      message: "Please enter a number",
                    },
                  }}
                />
              )}
              {newOwnershipDocument?.product?.category?.name === "Pšenica" && (
                <TextInputWithIcon
                  id="protein"
                  value={(newOwnershipDocument.protein as number) ?? ""}
                  onValueChange={handleNewOwnershipDocument}
                  labelText={t("Protein")}
                  icon="bi bi-clipboard"
                  iconAlign="right"
                  placeholderText={t(
                    "Unesite protein kao ceo broj, sa jednom ili dve decimale"
                  )}
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  rules={{
                    pattern: {
                      value: /^\d+(\.\d{1,2})?$/,
                      message: "Please enter a number",
                    },
                  }}
                />
              )}
              {newOwnershipDocument?.product?.category?.name === "Ječam" && (
                <div className="empty-field"></div>
              )}
              <RadioButtonInput
                id="calculationMethod"
                value={newOwnershipDocument.calculationMethod}
                radioButtonValue1={t("Svodjenje na SRPS")}
                radioButtonValue2={t("Neto")}
                onValueChange={handleNewOwnershipDocument}
                labelText={t("Metod obračuna")}
                control={control}
                errors={errors}
              />
              <div className="note-field">
                <TextAreaInput
                  id="note"
                  value={newOwnershipDocument.note as string}
                  onValueChange={handleNewOwnershipDocument}
                  rows={1}
                  cols={50}
                  labelText={t("Napomena")}
                  placeholderText=""
                  control={control}
                  setValue={setValue}
                  errors={errors}
                />
              </div>
              <div></div>
              <div className="footer-buttons">
                <ButtonInput
                  labelText={t("Sačuvaj")}
                  onButtonClick={handleSubmit(onSubmit, onError)}
                  addStyle={addButtonSaveStyle}
                />
                <ButtonInput
                  labelText={t("Resetuj")}
                  onButtonClick={handleResetForm}
                  addStyle={addButtonResetStyle}
                />
              </div>
            </div>
          )}
          <div className="documents-preview">
            <PdfPreview
              pdfDefinition={pdfData}
              downloadDocument={downloadPdfDocument}
              printDocument={printPdfDocument}
              documentName={`Vlasnički_list_${
                newOwnershipDocument?.ownerDocNumberFormatted ??
                newOwnershipDocument.ownerDocId ??
                ""
              }`}
            />
          </div>
        </div>
      </div>
    </PageLayout>
  );
};
